/**
 * Cutting a romanised name into pinyin syllables.
 *
 * A recogniser hands back "zeeyouon" or "shin yee" — letters with word boundaries invented
 * somewhere between the microphone and the name. The roster says "Ziyuan" and "Xinyi". No
 * amount of letter-level edit distance closes that gap honestly, because the letters are not
 * the unit of the name: the *syllables* are, and a Mandarin syllable has exactly two parts —
 * an initial (声母) and a final (韵母) — each of which a recogniser mangles in its own
 * predictable way.
 *
 * So this splits a run of letters into the syllables it could be made of. It is deliberately
 * a *segmenter*, not a validator: "shinyee" is not a legal pinyin string, and refusing to
 * segment it would throw away the exact case this exists for. Anything that cannot be read
 * as pinyin comes back as one opaque syllable with an empty initial, and the layers above
 * fall back to letter distance for it. That is how an English given name — "Emma", "Ryan" —
 * survives this file untouched.
 */

/**
 * Pinyin initials, longest first.
 *
 * Order matters: `zh` has to be tried before `z`, or every `zhang` segments as `z` + `hang`.
 */
const INITIALS = [
  'zh', 'ch', 'sh',
  'b', 'p', 'm', 'f', 'd', 't', 'n', 'l', 'g', 'k', 'h',
  'j', 'q', 'x', 'r', 'z', 'c', 's', 'y', 'w',
] as const;

/**
 * Pinyin finals, longest first.
 *
 * Includes the written-out forms a recogniser produces but pinyin never writes — `uei` for
 * `ui`, `iou` for `iu`, `uen` for `un`. Those are the underlying sounds; pinyin's spelling
 * of them is a contraction, and a recogniser hearing the sound spells it the long way.
 */
const FINALS = [
  'uang', 'iang', 'iong', 'uai', 'uei', 'iou', 'uen',
  'ang', 'eng', 'ong', 'ian', 'iao', 'uan', 'ing', 'van', 'ueng',
  'ai', 'ei', 'ao', 'ou', 'an', 'en', 'er', 'ia', 'ie', 'iu', 'in',
  'ua', 'uo', 'ui', 'un', 'ue', 've', 'vn',
  'a', 'o', 'e', 'i', 'u', 'v',
] as const;

/** A syllable, split into the two parts the confusion matrix scores separately. */
export interface Syllable {
  /** 声母 — may be empty for a zero-initial syllable ("an", "ou", "er"). */
  initial: string;
  /** 韵母 — the whole syllable when it could not be read as pinyin at all. */
  final: string;
  /** The syllable as written, before splitting. */
  raw: string;
  /** False when this is an opaque run of letters rather than a real pinyin syllable. */
  legal: boolean;
}

/** `ü` is typed a dozen ways. They are one vowel. */
function normaliseU(s: string): string {
  return s.replace(/ü/g, 'v').replace(/yu(?=[aeno]|$)/g, 'yv');
}

/**
 * Every way `word` reads as a run of legal syllables, or `null` when there is none.
 *
 * Ambiguity is real and unavoidable — "xian" is one syllable (xian) or two (xi + an), and
 * which one is right depends on a name this file has never seen. Both are returned and the
 * scorer above takes whichever fits the roster better. That is the only honest answer: the
 * segmenter cannot know, and guessing here would silently lose names.
 *
 * `strictDivider` narrows that for one caller. Hanyu Pinyin orthography (GB/T 16159) requires
 * an apostrophe before a syllable starting a-, e- or o- that is not word-initial: Xi'an is two
 * syllables *because* the apostrophe is there. That rule is authoritative for a roster row,
 * which a person typed, and useless for a transcript, which a recogniser produced and which
 * will never contain an apostrophe. So the roster side asks for it and the spoken side does
 * not. Without it on the roster side, "Cao" also read as ca + o, and since ch/c is a near-free
 * dialect substitution and a dropped initial is cheap, "chemo" scored higher against three
 * children surnamed Cao than against the child actually named.
 */
export function segmentations(word: string, limit = 24, strictDivider = false): Syllable[][] | null {
  // Where a syllable may begin with a bare a-, e- or o-. See `syllablesAt`: word-initially,
  // and wherever the writer put the syllable divider that the standard requires there.
  const breaks = new Set<number>([0]);
  let cleaned = '';
  for (const ch of word.toLowerCase()) {
    if (ch === "'" || ch === '\u2019') breaks.add(cleaned.length);
    else if (/[a-zü]/.test(ch)) cleaned += ch;
  }
  // normaliseU is length-preserving, so the break offsets survive it.
  const w = normaliseU(cleaned);
  if (w === '') return null;

  const out: Syllable[][] = [];

  const walk = (at: number, acc: Syllable[]) => {
    if (out.length >= limit) return;
    if (at === w.length) {
      out.push([...acc]);
      return;
    }
    for (const syl of syllablesAt(w, at, !strictDivider || breaks.has(at))) {
      acc.push(syl);
      walk(at + syl.raw.length, acc);
      acc.pop();
    }
  };
  walk(0, []);

  return out.length > 0 ? out : null;
}

/**
 * The legal pinyin syllables that could start at `at`, longest first, as written.
 *
 * Exposed for the romanisation table, which has to walk a token in the same units this file
 * walks it in: a name like "Tsienhua" is a variant spelling *glued to* a pinyin syllable, and
 * splitting it needs both alphabets on the same walk. `word` must already be lowercase ASCII.
 */
export function legalSyllablesAt(word: string, at: number): string[] {
  return syllablesAt(word, at, at === 0).map(syllable => syllable.raw);
}

/**
 * The legal syllables starting at `at`, longest first.
 *
 * `zeroInitialOk` carries the orthography rule that keeps this from inventing readings.
 * Hanyu Pinyin (GB/T 16159) requires an apostrophe before any syllable that begins with a-,
 * e- or o- and does not start the word — Xi'an is two syllables precisely because the
 * apostrophe is there, and "xian" without one is one syllable. Those bare-vowel finals are
 * exactly the zero-initial ones, so honouring the rule is a single condition here.
 *
 * Without it the segmenter read "Cao" as ca + o, and since ch/c is a near-free dialect
 * substitution and a dropped initial is cheap, "chemo" scored 0.78 against three children
 * surnamed Cao and 0.66 against the child actually named — the right answer ranked tenth
 * behind an orthography error.
 */
function syllablesAt(w: string, at: number, zeroInitialOk: boolean): Syllable[] {
  const found: Syllable[] = [];
  const rest = w.slice(at);

  for (const initial of ['', ...INITIALS]) {
    if (initial === '' && !zeroInitialOk) continue;
    if (initial !== '' && !rest.startsWith(initial)) continue;
    const afterInitial = rest.slice(initial.length);
    for (const final of FINALS) {
      if (!afterInitial.startsWith(final)) continue;
      if (!isLegal(initial, final)) continue;
      found.push({ initial, final, raw: initial + final, legal: true });
    }
  }

  // Longest first: a greedy reading is usually the right one, and `limit` in the caller
  // truncates the tail, so the good readings must come out before the bad ones.
  return found.sort((a, b) => b.raw.length - a.raw.length);
}

/**
 * Combinations pinyin does not spell.
 *
 * Kept loose on purpose. A strict phonotactic table would reject the misspellings this file
 * exists to read; all this rules out is the handful of splits that produce nonsense
 * segmentations of names that *do* segment correctly some other way.
 */
function isLegal(initial: string, final: string): boolean {
  // A bare vowel run with no initial is only a syllable for the finals that really start
  // words: "an", "ou", "er", "ai", "ao", "e", "a", "o", "ei", "en", "ang", "eng".
  if (initial === '') return /^(a|o|e|ai|ei|ao|ou|an|en|ang|eng|er)$/.test(final);
  // y- and w- are spelling devices for i- and u- finals; they never take a consonant final.
  if (initial === 'y') return /^(a|ao|an|ang|e|i|in|ing|o|ong|ou|u|un|uan|ue|v|ve|van|vn)$/.test(final);
  if (initial === 'w') return /^(a|ai|an|ang|ei|en|eng|o|u)$/.test(final);
  // j/q/x take only the palatal finals, and never the plain u series.
  if ('jqx'.includes(initial)) return /^(i|ia|ian|iang|iao|ie|in|ing|iong|iou|iu|u|uan|ue|un|v|van|ve|vn)$/.test(final);
  // zh/ch/sh/r/z/c/s never take an -i- glide.
  if (['zh', 'ch', 'sh', 'r', 'z', 'c', 's'].includes(initial)) return !/^i[aeo]/.test(final) && final !== 'v';
  return true;
}

/**
 * The syllables of one romanised word, best reading first, with an opaque fallback.
 *
 * The fallback is the important half. "Emma" is not pinyin and never will be; returning it
 * as a single illegal syllable lets the scorer treat it as a unit and fall back to letter
 * distance, rather than forcing it through a phonology it does not belong to.
 */
export function readings(word: string, strictDivider = false): Syllable[][] {
  const legal = segmentations(word, 24, strictDivider);
  const opaque: Syllable[] = [{ initial: '', final: word.toLowerCase(), raw: word.toLowerCase(), legal: false }];
  if (!legal) return [opaque];
  // The opaque reading stays available even for a word that segments: "an" is a legal
  // pinyin syllable and also the English word, and a one-letter roster name should not be
  // forced to compete as phonology.
  return [...legal, opaque];
}

/** The initials of a reading — "lsy" for Li Si Yuan. Deliverable: the initials index. */
export function initialsOf(syllables: readonly Syllable[]): string {
  return syllables.map(s => (s.legal ? s.initial || s.final[0] : s.raw[0]) ?? '').join('');
}
